import { z } from 'zod';

// Audited actions (CLAUDE.md: writes, reads of patient charts, exports, prints of
// clinical documents, logins, permission changes). Phase 0 has sign-in and
// administration; later phases add their own.
export const AUDIT_ACTIONS = [
  'auth.sign_in.succeeded',
  'auth.sign_in.failed',
  'auth.sign_in.locked',
  'auth.sign_out',
  'auth.facility.switched',
  'auth.two_factor.enrolled',
  'auth.two_factor.verified',
  'auth.two_factor.failed',
  'auth.two_factor.reset',
  'auth.recovery_code.used',
  'auth.password.changed',
  // Account security (ADR 0037). Metadata holds IDs and the link purpose or method only;
  // never a token, password or hash.
  'auth.password.change_failed',
  'auth.account_link.issued',
  'auth.account_link.used',
  'auth.two_factor.reset_requested',
  'auth.two_factor.reset_rejected',
  // The onboarding CLI creating a customer organisation (ADR 0037).
  'organisation.created',
  'user.created',
  'user.updated',
  'user.disabled',
  'user.enabled',
  'user.role.granted',
  'user.role.revoked',
  'role.created',
  'role.updated',
  'role.permission.granted',
  'role.permission.revoked',
  // A permission a release added to the role's template, declined by an administrator
  // (ADR 0031). Accepting one is audited as role.permission.granted.
  'role.permission.offer_declined',
  'facility.created',
  'facility.updated',
  'organisation.updated',
  // Appearance (ADR 0030): theme IDs and the mode only.
  'user.appearance.changed',
  'facility.default_theme.changed',
  // Phase 1: registration and visits (docs/plans/phase1-registration.md §2.15). Metadata
  // holds IDs, codes and counts only; a search records counts and match kinds, never terms.
  'patient.registered',
  'patient.updated',
  'patient.viewed',
  'patient.searched',
  'patient.entered_in_error',
  'patient.identifier.added',
  'patient.identifier.entered_in_error',
  'patient.contact.added',
  'patient.contact.entered_in_error',
  'patient.relationship.added',
  'patient.relationship.ended',
  'patient.consent.recorded',
  'patient.duplicate.overridden',
  'patient.registry.sync_requested',
  'patient.registry.sync_retried',
  'visit.opened',
  'visit.cancelled',
  'queue.entry.created',
  // Today's visits at a facility (docs/plans/phase1-home-and-demo-data.md §2): once per
  // session and facility within the patient-view window; IDs and counts only.
  'visit.list.viewed',
  'service_point.created',
  'service_point.updated',
  'facility.number_format.changed',
  'registration_settings.updated',
  'value_set.concept.created',
  'value_set.concept.updated',
  'consent_text.created',
  'consent_text.retired',
  // Sensitive records (plan §12, decision 15): who may see which label, and every
  // break-glass opening, with its reason.
  'sensitivity_label.created',
  'sensitivity_label.updated',
  'role.sensitivity_label.granted',
  'role.sensitivity_label.revoked',
  'record.break_glass',
  // Terminology and imports (docs/plans/phase1-terminology-and-import.md §4.5). Metadata
  // holds IDs, kind, format, counts and the file hash; never cell values or search text.
  'import.started',
  'import.validated',
  'import.committed',
  'import.failed',
  'import.cancelled',
  'import.report.downloaded',
  'terminology.version.activated',
  'terminology.code_system.created',
  // Finance master data (docs/plans/phase1-finance-catalogue.md §2.11). Metadata holds
  // IDs, codes, amounts in cents and counts.
  'catalogue.item.created',
  'catalogue.item.updated',
  'catalogue.item.deactivated',
  'catalogue.item.reactivated',
  'catalogue.item.code.added',
  'catalogue.item.code.entered_in_error',
  'catalogue.item.availability.changed',
  'price_list.created',
  'price_list.updated',
  'price.scheduled',
  'price.entered_in_error',
  'price_list.assignment.changed',
  'price_list.imported',
  'payer.created',
  'payer.updated',
  'scheme.created',
  'scheme.updated',
  'scheme.reviewed',
  'split_rule.scheduled',
  'split_rule.entered_in_error',
  'store.created',
  'store.updated',
  'supplier.created',
  'supplier.updated',
  'stock.item.details.changed',
  'stock.levels.changed',
  'stock.receipt.posted',
  'stock.receipt.short_expiry_accepted',
  'stock.transfer.posted',
  'stock.batch.overridden',
  'stock.adjustment.requested',
  'stock.adjustment.approved',
  'stock.adjustment.rejected',
  'stock.adjustment.cancelled',
  'stock.opening_balance.requested',
  'stock.opening_balance.approved',
  'stock.count.started',
  'stock.count.submitted',
  'stock.count.approved',
  'stock.count.rejected',
  'stock.count.cancelled',
  'stock.balances.verified',
  // Queues and triage (docs/plans/phase1-triage.md §2.14). Metadata holds IDs, codes,
  // counts and rule versions only; never complaint text, measured values or names.
  'queue.entry.called',
  'queue.entry.started',
  'queue.entry.finished',
  'queue.entry.returned',
  'queue.entry.cancelled',
  'queue.entry.transferred',
  'queue.entry.flags_changed',
  'queue.viewed',
  'queue_display.paired',
  'queue_display.updated',
  'queue_display.revoked',
  'vitals.recorded',
  'vitals.corrected',
  'vitals.entered_in_error',
  'triage.recorded',
  'triage.superseded',
  'triage.entered_in_error',
  'allergy.recorded',
  'allergy.entered_in_error',
  'allergy.no_known_recorded',
  'allergy.no_known_entered_in_error',
  'clinical_alert.acknowledged',
  'clinical_alert.overridden',
  'clinical_rule.created',
  'clinical_rule.updated',
  'clinical_rule.approved',
  'clinical_rule.retired',
  'clinical_settings.updated',
  'vital_coding.updated',
  'triage_category.updated',
  // Billing and the cashier (docs/plans/phase1-billing.md §2.12). Metadata holds IDs,
  // amounts in cents, methods and reason codes; never item names or patient details.
  'charge.created',
  'charge.created_manual',
  'charge.unpriced',
  'charge.priced_again',
  'charge.cancelled',
  'charge_adjustment.requested',
  'charge_adjustment.approved',
  'charge_adjustment.rejected',
  'charge_adjustment.withdrawn',
  'invoice.issued',
  'payment.recorded',
  'receipt.printed',
  'bill.printed',
  'cashier_shift.opened',
  'cashier_shift.closed',
  'cashier_shift.closed_by_supervisor',
  'credit_note.requested',
  'credit_note.approved',
  'credit_note.rejected',
  'credit_note.withdrawn',
  'refund.requested',
  'refund.approved',
  'refund.rejected',
  'refund.withdrawn',
  'refund.paid',
  'billing_settings.changed',
  'service_point.charge_item.changed',
  // Paybill (M-Pesa C2B) payments (ADR 0020).
  'mpesa_c2b.received',
  'mpesa_c2b.matched',
  'mpesa_c2b_endpoint.created',
  'mpesa_c2b_endpoint.revoked',
  // The lab (docs/plans/phase1-lab.md §2.10).
  'lab.section.created',
  'lab.section.updated',
  'lab.analyte.created',
  'lab.analyte.updated',
  'lab.analyte.option.changed',
  'lab.test.defined',
  'lab.test.updated',
  'lab.test.ready',
  'lab.order.placed',
  'lab.order.cancelled',
  'lab.order.entered_in_error',
  'lab.sample.collected',
  'lab.sample.label_printed',
  'lab.sample.received',
  'lab.sample.rejected',
  'lab.result.saved',
  'lab.report.submitted',
  'lab.report.returned',
  'lab.report.validated',
  'lab.report.released',
  'lab.report.corrected',
  'lab.report.entered_in_error',
  'lab.report.printed',
  'lab.report.viewed',
  'lab.critical.confirmed',
  'lab.critical.notified',
  'lab.critical.acknowledged',
  'lab.critical.escalated',
  'lab.critical.communicated',
  'lab.settings.changed',
  'lab.rules.changed',
  'lab.rules.approved',
  'lab_value_set.changed',
  // Lab analyzers (docs/plans/phase2-lab-connector.md §3.6). IDs, codes and counts only.
  'lab.analyzer.created',
  'lab.analyzer.updated',
  'lab.analyzer.credential_rotated',
  'lab.analyzer.retired',
  'lab.analyzer.test_codes_changed',
  'lab.analyzer.result_applied',
  'lab.analyzer.result_dismissed',
  // Lab QC and microbiology (docs/plans/phase3-lab-qc-and-microbiology.md §2). IDs, rule
  // keys, outcomes and counts only: never patient values, comments or notes.
  'lab.qc.material.saved',
  'lab.qc.lot.saved',
  'lab.qc.target.set',
  'lab.qc.target.retired',
  'lab.qc.rules.changed',
  'lab.qc.run.recorded',
  'lab.qc.run.entered_in_error',
  'lab.qc.corrective_action.recorded',
  'lab.qc.override.requested',
  'lab.qc.override.approved',
  'lab.qc.override.declined',
  'lab.micro.list.changed',
  'lab.culture.saved',
  'lab.culture.preliminary_issued',
  // The pharmacy (docs/plans/phase1-dispensing.md §4.10). IDs, numbers, quantities and
  // codes only: never item names with patient context, instructions or notes.
  'pharmacy.dispense.prepared',
  'pharmacy.dispense.dispensed',
  'pharmacy.dispense.cancelled',
  'pharmacy.substituted',
  'pharmacy.balance.closed',
  'pharmacy.label.printed',
  'pharmacy.counselling.recorded',
  'pharmacy.counselling.entered_in_error',
  'pharmacy.controlled.dispensed',
  'pharmacy.alert.overridden',
  'pharmacy.sale.completed',
  'pharmacy.external_prescription.recorded',
  'pharmacy.return.requested',
  'pharmacy.return.approved',
  'pharmacy.return.rejected',
  'pharmacy.return.withdrawn',
  'stock_item.supply_class.changed',
  'stock_item.barcode.changed',
  // Consultation and orders (docs/plans/phase1-consultation.md §2.22). Metadata holds IDs,
  // codes, counts and rule IDs only; never note text, clinical reasons typed into clinical
  // fields or anything a clinician wrote.
  'consultation.started',
  'consultation.finished',
  'consultation.entered_in_error',
  'note.signed',
  'note.amended',
  'note.entered_in_error',
  'note_template.changed',
  'diagnosis.recorded',
  'diagnosis.corrected',
  'diagnosis.entered_in_error',
  'problem.recorded',
  'problem.updated',
  'problem.entered_in_error',
  'allergy.verified',
  'allergy.updated',
  'visit.closed',
  'service_order.placed',
  'service_order.performed',
  'service_order.amended',
  'service_order.cancelled',
  'service_order.entered_in_error',
  // Reports (docs/plans/phase1-reports.md §7.4). Metadata: report key, facility IDs,
  // period, filter keys (never their values), row count, format. Never names, a single
  // patient's codes or amounts per patient. Aggregate views aren't audited per view.
  'report.viewed',
  'report.exported',
  'report.printed',
  // Prescribing and the formulary (docs/plans/phase1-consultation.md §2.22). Metadata holds
  // IDs, codes, counts, rule IDs and versions only; never doses or reasons typed.
  'formulary_item.created',
  'formulary_item.updated',
  'formulary_group.changed',
  'medication_frequencies.changed',
  'dose_rule.created',
  'dose_rule.updated',
  'dose_rule.approved',
  'dose_rule.retired',
  'prescription.signed',
  'prescription.printed',
  'prescription_line.stopped',
  'prescription_line.cancelled',
  'prescription_line.entered_in_error',
  // SHA (docs/plans/phase1-sha.md §4.11). Metadata holds IDs, codes, counts and amounts in
  // cents; never names, identifiers typed in a search, or justification text.
  'sha.eligibility.checked',
  'sha.eligibility.recorded_by_hand',
  'sha.visit.started',
  'sha.visit.start_failed',
  'registry.facility.searched',
  'registry.facility.linked',
  'registry.facility.refreshed',
  'registry.practitioner.searched',
  'registry.practitioner.linked',
  // The local practitioner profile (consultation plan §3.2): cadre, licence and status.
  'practitioner.changed',
  // Staff records with licence expiry (docs/plans/phase2-staff-records.md §3): IDs, codes,
  // counts and field names only, never names, licence numbers or departments.
  'staff.list.viewed',
  'staff.licences.viewed',
  'staff.record.viewed',
  'staff.record.changed',
  'staff.document.uploaded',
  'staff.document.viewed',
  'staff.document.downloaded',
  'staff.document.entered_in_error',
  'staff.licence_expiry.recorded',
  'staff.licence_notice.seen',
  // KHIS submissions and quality targets (docs/plans/phase3-khis-and-quality.md §3).
  // Metadata holds IDs, periods, counts, UIDs and indicator keys; never reasons.
  'khis.mapping.draft_created',
  'khis.mapping.draft_saved',
  'khis.mapping.activated',
  'khis.org_unit.set',
  'khis.submission.prepared',
  'khis.submission.approved',
  'khis.submission.discarded',
  'khis.submission.send_again',
  'khis.submission.superseded',
  'quality.target.set',
  // Accounting integration and doctor revenue share
  // (docs/plans/phase3-accounting-and-revenue-share.md §4). Metadata holds IDs, dates,
  // formats, counts and cents; never names.
  'accounting.account.mapped',
  'accounting.journal.viewed',
  'accounting.journal.exported',
  'accounting.period.closed',
  'revenue_share.basis.changed',
  'revenue_share.rule.created',
  'revenue_share.rule.ended',
  'revenue_share.statement.prepared',
  'revenue_share.statement.viewed',
  'revenue_share.statement.approved',
  'revenue_share.statement.rejected',
  'revenue_share.statement.withdrawn',
  'revenue_share.payout.recorded',
  // Clinical documents (consultation plan §2.22). Metadata holds IDs, numbers of days,
  // copy numbers, kinds, content types and sizes; never titles, file names, remarks,
  // reasons or the referral summary.
  'sick_sheet.issued',
  'sick_sheet.printed',
  'sick_sheet.entered_in_error',
  'referral.issued',
  'referral.printed',
  'referral.entered_in_error',
  'document.uploaded',
  'document.viewed',
  'document.downloaded',
  'document.entered_in_error',
  'preauth.requested',
  'preauth.cancelled',
  'preauth.overridden',
  // Work done under a recorded override (collect, perform, dispense); ruleId in metadata.
  'preauth.override_used',
  'preauth.status_changed',
  'preauth_list.imported',
  'preauth_list.activated',
  'claim.built',
  'claim.submitted',
  'claim.status_changed',
  'claim.assigned',
  'claim.resubmitted',
  'claim.viewed',
  'claim.printed',
  'claim.exported',
  'claim.write_off.requested',
  'claim.write_off.approved',
  'claim.write_off.rejected',
  'sha_settings.changed',
  // Appointments and SMS reminders (docs/plans/phase2-appointments.md §3.4). Metadata holds
  // IDs, codes, instants and counts; reasons go in the reason field; notes never.
  'appointment_schedule.created',
  'appointment_schedule.updated',
  'appointment.booked',
  'appointment.rescheduled',
  'appointment.cancelled',
  'appointment.no_show',
  'appointment.arrived',
  'appointment.list.viewed',
  'patient.reminder_consent.recorded',
  // Private insurance (docs/plans/phase2-private-insurance.md §7). Metadata holds IDs, codes,
  // counts and cents; never member numbers, principal names or notes.
  'coverage.recorded',
  'coverage.ended',
  'coverage.entered_in_error',
  'benefit_rule.created',
  'benefit_rule.deactivated',
  'charge.benefit_applied',
  'insurer_preauth.recorded',
  'insurer_preauth.decided',
  'insurer_preauth.cancelled',
  'insurer_claim.prepared',
  'insurer_claim.viewed',
  'insurer_claim.submitted',
  'insurer_claim.rejected',
  'insurer_claim.resubmitted',
  'insurer_claim.printed',
  'insurer_claim.entered_in_error',
  'insurer_invoice.issued',
  'insurer_invoice.printed',
  'insurer_remittance.recorded',
  'insurer_remittance.allocated',
  'insurer_claim.write_off.requested',
  'insurer_claim.write_off.approved',
  'insurer_claim.write_off.rejected',
  // Inpatient ADT (docs/plans/phase2-inpatient-adt.md §2.8). Metadata holds IDs, codes,
  // counts and dates; never the admission reason or discharge text.
  'ward.created',
  'ward.updated',
  'ward_room.created',
  'ward_room.updated',
  'bed.created',
  'bed.updated',
  'bed.status_changed',
  'admission.created',
  'admission.bed_assigned',
  'admission.moved',
  'admission.discharged',
  'admission.cancelled',
  'admission.bed_days_posted',
  // Once per session and facility within the patient-view window, like visit.list.viewed.
  'inpatient.bed_board.viewed',
  // Inpatient billing (docs/plans/phase2-inpatient-billing.md §2.8). IDs, cents, counts
  // and codes only.
  'deposit.taken',
  'deposit.applied',
  'inpatient_bill.interim_issued',
  'inpatient_bill.final_issued',
  'inpatient_bill.printed',
  'package.created',
  'package.updated',
  'admission.package_applied',
  'admission.package_removed',
  'package.charges_covered',
  'admission.discharged_with_balance',
  // Ward rounds and nursing (docs/plans/phase2-ward-rounds-nursing.md §2.5). IDs and counts
  // only; never note or handover text.
  'ward_round.started',
  'nursing_note.recorded',
  'nursing_note.entered_in_error',
  'handover_note.recorded',
  'handover_note.entered_in_error',
  // Once per session and ward within the patient-view window, like the bed board.
  'ward.worklist.viewed',
  // eMAR and inpatient pharmacy (docs/plans/phase2-emar-inpatient-pharmacy.md §2.5). IDs,
  // outcomes, timing and supply only; never a reason or note.
  'medication_order.verified',
  'medication_order.verification_entered_in_error',
  'medication.administered',
  'medication.administration_entered_in_error',
  'medication.alert.overridden',
  'emar.settings.changed',
  // Casualty and unknown patients (docs/plans/phase2-casualty.md §2.4). IDs and booleans
  // only; never the description, the reason or a name.
  'casualty.unknown_patient.registered',
  'casualty.unknown_list.viewed',
  'patient.merge.requested',
  'patient.merge.approved',
  'patient.merge.rejected',
  'patient.merge.withdrawn',
  'patient.merged',
  // Death and birth notification, and the mortuary
  // (docs/plans/phase2-mortuary-and-notifications.md §2.7). IDs, place and manner codes,
  // counts and cents only; never causes, names, collector details, notes or weights.
  'death.recorded',
  // A deceased patient refused a visit, booking, order or dose; refused action in metadata.
  'patient.deceased_refused',
  'death.correction.requested',
  'death.correction.approved',
  'death.correction.rejected',
  'death.correction.withdrawn',
  'death.entered_in_error',
  'death.printed',
  // Once per session and facility within the patient-view window.
  'death.register.viewed',
  'birth.recorded',
  'birth.entered_in_error',
  'birth.printed',
  'birth.register.viewed',
  'mortuary.case.received',
  'mortuary.case.moved',
  'mortuary.release.requested',
  'mortuary.release.approved',
  'mortuary.release.rejected',
  'mortuary.release.withdrawn',
  'mortuary.case.released',
  'mortuary.case.released_with_balance',
  'mortuary.case.cancelled',
  'mortuary.storage_days_posted',
  'mortuary.board.viewed',
  'mortuary.slot.created',
  'mortuary.slot.updated',
  'mortuary.settings.updated',
  'appointment_reminder.skipped_deceased',
  // Procurement (docs/plans/phase2-procurement.md §2.10). IDs, numbers, counts, flag codes
  // and cents only.
  'stock.requisition.requested',
  'stock.requisition.issued',
  'stock.requisition.closed',
  'stock.requisition.rejected',
  'stock.requisition.cancelled',
  'procurement.requisition.requested',
  'procurement.requisition.approved',
  'procurement.requisition.rejected',
  'procurement.requisition.cancelled',
  'procurement.order.submitted',
  'procurement.order.approved',
  'procurement.order.rejected',
  'procurement.order.cancelled',
  'procurement.order.closed',
  'procurement.order.printed',
  'stock.receipt.over_receipt_accepted',
  'stock.receipt.reversal_requested',
  'stock.receipt.reversed',
  'stock.receipt.reversal_rejected',
  'stock.receipt.reversal_cancelled',
  'procurement.invoice.recorded',
  'procurement.invoice.approved',
  'procurement.invoice.rejected',
  'procurement.invoice.cancelled',
  // Medical records and data-subject rights (docs/plans/phase2-records-data-rights.md §2.6).
  // IDs, kinds, formats, outcomes and counts only; never details, names, responses,
  // recipients or reasons typed.
  'records_request.received',
  'records_request.identity_verified',
  'records_request.change_linked',
  'records_request.closed',
  'record.exported',
  'record.printed',
  // Reading a patient's access log (the audit log itself), every time.
  'patient.access_log.viewed',
  // Opening the facility's release log (IDs and counts only).
  'record_releases.viewed',
  // Radiology RIS lite (docs/plans/phase2-radiology.md §2.6). IDs, versions, counts and
  // flags only; never findings, impressions, notes or links.
  'imaging.exam.scheduled',
  'imaging.exam.rescheduled',
  'imaging.exam.images_linked',
  'imaging.report.signed',
  'imaging.report.verified',
  'imaging.report.amended',
  'imaging.report.printed',
  'imaging_procedure.updated',
  // Theatre lite (docs/plans/phase2-theatre.md §2.8). IDs, codes, counts and times only;
  // never booking notes, checklist notes, consent reasons or note text.
  'theatre.created',
  'theatre.updated',
  'theatre_checklist_item.created',
  'theatre_checklist_item.updated',
  'theatre_case.booked',
  'theatre_case.rescheduled',
  'theatre_case.cancelled',
  'theatre_case.checklist_recorded',
  'theatre_case.signed_in',
  'theatre_case.signed_out',
  'theatre_case.note_signed',
  'theatre_case.note_amended',
  'theatre_case.charge_recorded',
  'theatre_case.charge_cancelled',
  // Once per session and facility within the patient-view window, like the bed board.
  'theatre.list.viewed',
  // Referrals (docs/plans/phase2-referrals.md §2.6). IDs, kinds, channels, counts and flags
  // only; never reasons, notes, summaries, recommendations or typed facility names.
  'referral.received',
  'referral.updated',
  'referral_record.entered_in_error',
  'referral_feedback.issued',
  'referral_feedback.printed',
  'referral_feedback.entered_in_error',
  // Once per session and facility within the patient-view window, like the theatre list.
  'referrals.list.viewed',
  'referrals.exported',
  // Maternity and MCH (docs/plans/phase2-maternity-mch.md §2.11). IDs, codes, counts and
  // flags only; never notes, danger-sign words or measured values.
  'pregnancy.opened',
  'pregnancy.corrected',
  'pregnancy.closed',
  'pregnancy.entered_in_error',
  'anc_contact.recorded',
  'anc_contact.entered_in_error',
  'labour.opened',
  'labour.closed',
  'labour.entered_in_error',
  // An interruptive maternity alert answered with a reason (rule ID in metadata; plan M25).
  'maternity.alert.overridden',
  'labour_observation.recorded',
  'labour_observation.entered_in_error',
  'delivery.recorded',
  'delivery.entered_in_error',
  'delivery_baby.birth_notification_linked',
  'postnatal_check.recorded',
  'postnatal_check.entered_in_error',
  'immunisation.recorded',
  'immunisation.entered_in_error',
  'maternity.settings.changed',
  'vaccine.saved',
  'immunisation_schedule.saved',
  // Once per session and facility within the patient-view window, like the theatre list.
  'labour_ward.viewed',
  // Chart tracking (docs/plans/phase2-medical-records-charts.md §3). IDs and kinds only;
  // never names or reasons.
  'chart.opened',
  'chart.issued',
  'chart.returned',
  // Once per session and facility within the patient-view window, like the registers.
  'chart.list.viewed',
  'chart_location.saved',
  'records.settings.updated',
  // Patient portal (docs/plans/phase3-portal-feedback-kiswahili.md §3.4; ADR 0035). The actor
  // of a portal action is the portal account; metadata names the patient so the event shows
  // in their access log. IDs and codes only: never the phone number or the code.
  'portal.account.enrolled',
  'portal.account.suspended',
  'portal.account.resumed',
  'portal.account.closed',
  'portal.dependant.linked',
  'portal.dependant.ended',
  'portal.sign_in.code_requested',
  'portal.sign_in.code_sent',
  'portal.sign_in.succeeded',
  'portal.sign_in.failed',
  'portal.sign_in.locked',
  'portal.sign_out',
  'portal.language.changed',
  'portal.appointments.viewed',
  'portal.appointment.cancelled',
  'portal.bills.viewed',
  'portal.results.viewed',
  'portal.allergies.viewed',
  'portal.feedback.viewed',
  'portal.settings.updated',
  'portal.consent_text.created',
  'facility.paybill.changed',
  // Patient feedback (plan §5): reading a comment is audited like a chart read.
  'feedback.requested',
  'feedback.submitted',
  'feedback.viewed',
  'feedback.replied',
  'feedback.resolved',
  'feedback.escalated',
  // The staff interface language (plan §8).
  'user.language.changed',
  // Operations modules (docs/plans/phase3-operations-modules.md). IDs, codes and counts only;
  // never what happened, root causes, notes or names.
  'operations_list.saved',
  'incident.reported',
  'incident.list.viewed',
  'incident.viewed',
  'incident.status.changed',
  'incident.root_cause.changed',
  'incident.action.added',
  'incident.action.completed',
  'incident.action.cancelled',
  'incident.entered_in_error',
  'isolation_flag.started',
  'isolation_flag.ended',
  'isolation_flag.entered_in_error',
  'infection_event.recorded',
  'infection_event.entered_in_error',
  'infection.line_list.viewed',
  'asset.registered',
  'asset.changed',
  'asset.status.changed',
  'work_order.opened',
  'work_order.status.changed',
  'work_order.part.used',
  'shift_definitions.saved',
  'roster.entry.added',
  'roster.entry.removed',
  'roster.swap.requested',
  'roster.swap.approved',
  'roster.swap.rejected',
  'attendance.clocked_in',
  'attendance.clocked_out',
  'attendance.report.viewed',
  // Diet orders and the kitchen (docs/plans/phase3-kitchen-and-housekeeping.md §1.3). IDs,
  // codes and counts; never the kitchen note.
  'diet_order.placed',
  'diet_order.entered_in_error',
  'ward.diets.viewed',
  'kitchen.meal_list.viewed',
  'kitchen.meal_tickets.printed',
  // Housekeeping (plan §2.3). No patient is named; notes are never audited.
  'housekeeping.area.saved',
  'housekeeping.schedule.saved',
  'housekeeping.task.started',
  'housekeeping.task.completed',
  'housekeeping.task.skipped',
  'housekeeping.consumable.used',
  'bed.cleaning_overridden',
  // Privacy tooling (docs/plans/golive-privacy-tooling.md). Label changes carry the reason
  // in the audit reason and label, visit and patient IDs in metadata; reading the audit log
  // and the break-glass report records filters and counts only; a retention run records
  // counts only.
  'visit.label.applied',
  'visit.label.removed',
  'break_glass.report.viewed',
  'break_glass.reviewed',
  'audit.events.viewed',
  'audit.chain.verified',
  'retention.rule.changed',
  'retention.purge.ran',
  'records_request.reviewed',
  // eTIMS signing modes (docs/plans/phase3-etims-vscu.md §3): the mode before and after with
  // the reason; a failed document signed again, by signing ID, type and attempt.
  'etims.settings.changed',
  'etims.sign_again',
] as const;

export type AuditAction = (typeof AUDIT_ACTIONS)[number];

export const auditEventSchema = z.object({
  id: z.uuid(),
  seq: z.number().int(),
  occurredAt: z.iso.datetime(),
  actorId: z.uuid(),
  /** The actor's display name; "System" for the system actor. */
  actorName: z.string(),
  action: z.string(),
  entityType: z.string().nullable(),
  entityId: z.uuid().nullable(),
  facilityId: z.uuid().nullable(),
  reason: z.string().nullable(),
  ip: z.string().nullable(),
  metadata: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
});

export type AuditEventView = z.infer<typeof auditEventSchema>;

export const auditChainStatusSchema = z.object({
  checked: z.number().int(),
  intact: z.boolean(),
  firstBrokenSeq: z.number().int().nullable(),
});
